import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers } from '@/components/icons';
import { usePortfolios } from '@/features/portfolios/api/use-portfolios';
import { MAX_HITS_PER_GROUP, type CommandEntityHit } from './types';

/**
 * Portfolio entity-search adapter (W129-F5-S2).
 *
 * Reads the already-cached `usePortfolios()` result and filters client-side
 * by case-insensitive substring on `name`. Same shape + same empty-query
 * rule as the buildings adapter (see ./buildings.ts).
 */
export function usePortfoliosCommandHits(query: string): readonly CommandEntityHit[] {
  const portfoliosQuery = usePortfolios();
  const navigate = useNavigate();

  return useMemo<readonly CommandEntityHit[]>(() => {
    const trimmed = query.trim();
    if (!trimmed) return [];
    const items = portfoliosQuery.data?.items ?? [];
    if (items.length === 0) return [];

    const needle = trimmed.toLowerCase();
    const matched = items.filter((p) =>
      (p.name ?? '').toLowerCase().includes(needle),
    );
    return matched.slice(0, MAX_HITS_PER_GROUP).map<CommandEntityHit>((p) => ({
      id: `portfolio:${p.id}`,
      label: p.name ?? p.id,
      sublabel: p.kind ?? undefined,
      kind: 'portfolios',
      icon: Layers,
      onSelect: () => navigate(`/portfolios/${p.id}`),
    }));
  }, [query, portfoliosQuery.data, navigate]);
}
